import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, HelpCircle, ArrowRight } from "lucide-react";

import { SiteHeader, SiteFooter } from "@/components/site-chrome";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { guides, questions, resources } from "@/content/resources";
import { breadcrumbJsonLd, SITE_URL, BRAND_OG_IMAGE, BRAND_OG_IMAGE_ALT, SITE_NAME } from "@/lib/seo";

const SITE = SITE_URL;

export const Route = createFileRoute("/resources/")({
  head: () => ({
    meta: [
      { title: "Parent Guides: English, Phonics & Tutoring | Smart Word Labs" },
      {
        name: "description",
        content:
          "Practical guides and quick answers for parents: teaching phonics at home, spotting reading gaps, tutoring costs, and preparing for school interviews.",
      },
      {
        property: "og:title",
        content: "Parent Resources: English, Phonics & Tutoring Guides",
      },
      {
        property: "og:description",
        content:
          "Practical guides and quick answers for parents of children aged 4-12 learning English.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: `${SITE}/resources` },
      { property: "og:site_name", content: SITE_NAME },
      { property: "og:image", content: BRAND_OG_IMAGE },
      { property: "og:image:alt", content: BRAND_OG_IMAGE_ALT },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: BRAND_OG_IMAGE },
      { name: "twitter:image:alt", content: BRAND_OG_IMAGE_ALT },
    ],
    links: [{ rel: "canonical", href: `${SITE}/resources` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "Smart Word Labs Parent Resources",
          url: `${SITE}/resources`,
          blogPost: resources.map((r) => ({
            "@type": "BlogPosting",
            headline: r.title,
            description: r.description,
            url: `${SITE}/resources/${r.slug}`,
            dateModified: r.updated,
          })),
        }),
      },
      breadcrumbJsonLd([{ name: "Parent Resources", path: "/resources" }]),
    ],
  }),
  component: ResourcesIndex,
});

function ResourcesIndex() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <Breadcrumbs trail={[{ name: "Parent Resources", path: "/resources" }]} />

      <main>
        <section className="gradient-hero border-b border-border">
          <div className="mx-auto max-w-4xl px-5 py-16 text-center sm:py-20">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
              <BookOpen className="size-3.5" /> Parent resources
            </span>
            <h1 className="mt-5 font-display text-4xl font-extrabold text-primary sm:text-5xl">
              Straight answers for parents of young English learners
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
              Short, practical guidance from our young-learner specialists — phonics at home,
              spotting reading gaps, what tutoring costs, and how to prepare for a school
              interview. No jargon, no upsell.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-5 py-16">
          <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">
            Guides
          </h2>
          <p className="mt-2 text-muted-foreground">In-depth reads you can act on this week.</p>

          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {guides.map((g) => (
              <article
                key={g.slug}
                className="flex flex-col rounded-2xl border border-border bg-card p-6 transition-shadow hover:shadow-lg"
              >
                <span className="text-xs font-semibold uppercase tracking-wide text-accent-foreground/80">
                  {g.topic} · {g.readMinutes} min read
                </span>
                <h3 className="mt-3 font-display text-xl font-bold text-primary">
                  <Link to="/resources/$slug" params={{ slug: g.slug }} className="hover:underline">
                    {g.title}
                  </Link>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                  {g.description}
                </p>
                <Link
                  to="/resources/$slug"
                  params={{ slug: g.slug }}
                  className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5"
                >
                  Read the guide <ArrowRight className="size-4" />
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="border-t border-border bg-muted/30">
          <div className="mx-auto max-w-4xl px-5 py-16">
            <h2 className="font-display text-2xl font-extrabold text-primary sm:text-3xl">
              Quick answers
            </h2>
            <p className="mt-2 text-muted-foreground">
              Common questions from parents, answered in a minute or two.
            </p>

            <div className="mt-8 space-y-4">
              {questions.map((q) => (
                <article
                  key={q.slug}
                  className="rounded-2xl border border-border bg-card p-6"
                >
                  <h3 className="flex items-start gap-3 font-display text-lg font-bold text-primary">
                    <HelpCircle className="mt-0.5 size-5 shrink-0 text-accent-foreground/70" />
                    <Link
                      to="/resources/$slug"
                      params={{ slug: q.slug }}
                      className="hover:underline"
                    >
                      {q.question ?? q.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {q.shortAnswer}
                  </p>
                  <Link
                    to="/resources/$slug"
                    params={{ slug: q.slug }}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5"
                  >
                    Full answer <ArrowRight className="size-4" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
